/**
 * @fileoverview Scores drawer — explains how the three live scores (Balance,
 * Comfort, Efficiency) are computed and how to improve them. Mirrors the
 * TheoryDrawer pattern (right-side Drawer + Collapse + LatexRenderer) and the
 * formulas match physics.js (balanceScore / comfortScore / efficiencyScore).
 *
 * @module HydraulicBalancingTesting/components/ScoresDrawer
 */

import React from 'react';
import { Drawer, Collapse } from 'antd';

import { LatexRenderer } from '../../../Shared/LatexRenderer';

import styles from '../HydraulicBalancingTesting.module.css';

const SCORE_TOPICS = [
  {
    key: 'balance',
    title: 'Balance — flow vs. design',
    blocks: [
      { type: 'text', text: 'Measured under commissioning conditions: the network is re-solved with every thermostatic head forced fully open, so you see the raw hydraulics without the thermostats hiding an imbalance.' },
      { type: 'text', text: 'For each room the flow deviation is the relative gap between the actual flow and the calculated design flow:' },
      { type: 'latex', latex: '$ dev = \\dfrac{\\dot{V}_{actual} - \\dot{V}_{design}}{\\dot{V}_{design}} $' },
      { type: 'text', text: 'The score grades the mean absolute deviation across all nine rooms, hitting 0 at an average deviation of 50%:' },
      { type: 'latex', latex: '$ score = 100\\cdot\\left(1 - \\dfrac{\\overline{|dev|}}{0.5}\\right) $' },
    ],
    improve: [
      'Lower the preset on overflowing rooms (deviation shown as +%, e.g. rooms near the pump).',
      'Raise the preset on starved rooms (deviation shown as −%, e.g. the far/top rooms).',
      'Work iteratively — every valve you touch shifts the flow in ALL the other rooms.',
      'Use the “Flow deviation” overlay and turn on Commissioning mode to see the effect clearly.',
    ],
  },
  {
    key: 'comfort',
    title: 'Comfort — rooms at setpoint',
    blocks: [
      { type: 'text', text: 'A live measure of how close every room is to its thermostat setpoint right now. Each room scores 1.0 when it is on target and drops to 0 once it is 3 K away; the score is the average across all nine rooms:' },
      { type: 'latex', latex: '$ score = 100\\cdot\\dfrac{1}{N}\\sum_{rooms}\\mathrm{clamp}\\!\\left(1 - \\dfrac{|T - T_{set}|}{3},\\,0,\\,1\\right) $' },
    ],
    improve: [
      'Make sure each room can actually reach its setpoint: enough radiator size, supply temperature and flow.',
      'A balanced network is the prerequisite — starved rooms can never warm up no matter the curve.',
      'Let the simulation settle: temperatures take time to respond after a change.',
    ],
  },
  {
    key: 'efficiency',
    title: 'Efficiency — COP & pump power',
    blocks: [
      { type: 'text', text: 'Efficiency combines two things you control: how efficiently the heat pump runs (the COP part, weighted 80%) and how much electricity the circulation pump wastes (the pump part, weighted 20%):' },
      { type: 'latex', latex: '$ score = 100\\cdot\\left(0.8\\cdot copPart + 0.2\\cdot pumpPart\\right) $' },

      { type: 'subtitle', text: 'COP part (80%)' },
      { type: 'text', text: 'This grades the current COP between the worst and best COP that are physically achievable at the current outdoor temperature — so you are always measured against what is possible today, not an absolute number:' },
      { type: 'latex', latex: '$ copPart = \\mathrm{clamp}\\!\\left(\\dfrac{COP - COP_{worst}}{COP_{best} - COP_{worst}},\\,0,\\,1\\right) $' },
      { type: 'text', text: 'COP_best is taken at the lowest useful supply temperature (≈30 °C) and COP_worst at the maximum (60 °C), both at the current outdoor temperature. Because every Kelvin of supply temperature is worth ≈2–3% COP, lowering the supply / flattening the heating curve pushes COP toward COP_best and the part toward 1.' },

      { type: 'subtitle', text: 'Pump part (20%)' },
      { type: 'text', text: 'This penalises circulation electricity, falling linearly from 1 (no pump power) to 0 once the pump draws 120 W:' },
      { type: 'latex', latex: '$ pumpPart = \\mathrm{clamp}\\!\\left(1 - \\dfrac{P_{pump}}{120\\,\\mathrm{W}},\\,0,\\,1\\right) $' },
      { type: 'text', text: 'Pump power rises with head and flow (P ≈ V̇·Δp/η). Once the system is balanced you can lower the pump head, which cuts P_pump and lifts this part.' },
    ],
    improve: [
      'Lower the supply temperature (or flatten the heating curve): every 1 K is worth ≈2–3% COP — this drives the larger 80% COP part.',
      'After balancing, reduce the pump head — an oversized head burns electricity around the clock.',
      'A low return temperature is the signature of an efficient, well-balanced heat-pump system.',
    ],
  },
];

const Block = ({ block }) => {
  switch (block.type) {
    case 'text':
      return <p className={styles.theoryText}>{block.text}</p>;
    case 'subtitle':
      return <p className={styles.sectionLabel} style={{ marginTop: 12 }}>{block.text}</p>;
    case 'latex':
      return (
        <div className={styles.theoryFormula}>
          <LatexRenderer latex={block.latex} />
        </div>
      );
    default:
      return null;
  }
};

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 */
export const ScoresDrawer = ({ open, onClose }) => (
  <Drawer
    title="Scores — how they are calculated and improved"
    placement="right"
    width={672}
    open={open}
    onClose={onClose}
  >
    <Collapse
      defaultActiveKey={SCORE_TOPICS.map((t) => t.key)}
      items={SCORE_TOPICS.map((topic) => ({
        key: topic.key,
        label: <b style={{ color: '#262626' }}>{topic.title}</b>,
        children: (
          <>
            <p className={styles.sectionLabel}>How it's calculated</p>
            {topic.blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
            <p className={styles.sectionLabel} style={{ marginTop: 12 }}>How to improve it</p>
            <ul className={styles.theoryList}>
              {topic.improve.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </>
        ),
      }))}
    />
  </Drawer>
);
